import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  BarChart3, 
  Users, 
  Eye, 
  Clock, 
  ArrowUpDown, 
  Globe,
  Settings,
  TrendingUp,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TICK_SMALL = { fontSize: 11, fill: '#999' };
const TICK_CATEGORY = { fontSize: 11, fill: '#666' };

export default function GoogleAnalytics() {
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [sources, setSources] = useState([]);
  const [topPages, setTopPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [configClientId, setConfigClientId] = useState('');
  const [configPropertyId, setConfigPropertyId] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const { data } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(data);
    } catch (e) {
      toast.error('Impossible de charger les clients');
    }
  };

  const fetchAnalytics = async (clientId, period) => {
    if (!clientId) return;
    setLoading(true);
    setError('');
    try {
      const [overviewRes, dailyRes, sourcesRes, pagesRes] = await Promise.all([
        axios.get(`${API}/ga/client/${clientId}/overview?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/ga/client/${clientId}/daily?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/ga/client/${clientId}/sources?days=${period}`, { withCredentials: true }),
        axios.get(`${API}/ga/client/${clientId}/pages?days=${period}`, { withCredentials: true }),
      ]);
      setOverview(overviewRes.data);
      setDailyData(dailyRes.data);
      setSources(sourcesRes.data);
      setTopPages(pagesRes.data);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur lors du chargement';
      setError(msg);
      setOverview(null);
      setDailyData([]);
      setSources([]);
      setTopPages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClientChange = (clientId) => {
    setSelectedClientId(clientId);
    const client = clients.find(c => c.id === clientId);
    if (client && !client.ga_property_id) {
      // Client not configured - show config dialog instead of fetching
      setError('');
      setOverview(null);
      openConfig(clientId);
    } else {
      fetchAnalytics(clientId, days);
    }
  };

  const handleDaysChange = (newDays) => {
    setDays(newDays);
    if (selectedClientId) fetchAnalytics(selectedClientId, newDays);
  };

  const handleSaveProperty = async () => {
    try {
      await axios.put(`${API}/ga/client/${configClientId}/property`, {
        ga_property_id: configPropertyId
      }, { withCredentials: true });
      setConfigDialogOpen(false);
      setConfigPropertyId('');
      // Refresh clients list to update ga_property_id
      const { data: updatedClients } = await axios.get(`${API}/clients`, { withCredentials: true });
      setClients(updatedClients);
      // Auto-fetch analytics if property was set
      if (configPropertyId.trim() && configClientId === selectedClientId) {
        fetchAnalytics(selectedClientId, days);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Une erreur est survenue");
    }
  };

  const openConfig = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setConfigClientId(clientId);
    setConfigPropertyId(client?.ga_property_id || '');
    setConfigDialogOpen(true);
  };

  const formatDate = (d) => {
    const parts = d.split('-');
    return `${parts[2]}/${parts[1]}`;
  };

  const formatDuration = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
  };

  const selectedClient = clients.find(c => c.id === selectedClientId);

  return (
    <Layout>
      <div className="space-y-6" data-testid="ga-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">
              Google Analytics
            </h1>
            <p className="text-neutral-500 mt-1">Trafic et performance de vos clients</p>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
          <div className="flex-1 max-w-xs">
            <Select value={selectedClientId} onValueChange={handleClientChange}>
              <SelectTrigger data-testid="ga-client-select">
                <SelectValue placeholder="Sélectionner un client..." />
              </SelectTrigger>
              <SelectContent>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} {c.ga_property_id ? '' : '(non configuré)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            {[7, 30, 90].map(d => (
              <Button
                key={d}
                variant={days === d ? 'default' : 'outline'}
                size="sm"
                className={days === d ? 'bg-[#002FA7] hover:bg-[#002277]' : ''}
                onClick={() => handleDaysChange(d)}
                data-testid={`ga-days-${d}`}
              >
                {d}j
              </Button>
            ))}
          </div>
          {selectedClientId && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => openConfig(selectedClientId)}
              data-testid="ga-config-btn"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configurer GA
            </Button>
          )}
        </div>

        {/* No client selected */}
        {!selectedClientId && (
          <div className="bg-white border border-neutral-200 p-12 text-center">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-500">Sélectionnez un client pour voir ses données Analytics</p>
            <p className="text-sm text-neutral-400 mt-1">
              Chaque client doit avoir un ID de propriété GA4 configuré
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className={`border rounded p-4 flex items-start gap-3 ${error.includes('non configuré') ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`} data-testid="ga-error">
            <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${error.includes('non configuré') ? 'text-amber-600' : 'text-red-600'}`} />
            <div>
              <p className={`text-sm font-medium ${error.includes('non configuré') ? 'text-amber-800' : 'text-red-800'}`}>
                {error.includes('non configuré') ? 'Configuration requise' : 'Erreur de chargement'}
              </p>
              <p className={`text-sm mt-1 ${error.includes('non configuré') ? 'text-amber-700' : 'text-red-700'}`}>{error}</p>
              {selectedClientId && error.includes('non configuré') && (
                <Button variant="outline" size="sm" className="mt-3" onClick={() => openConfig(selectedClientId)}>
                  <Settings className="w-4 h-4 mr-2" />
                  Configurer la propriété GA4
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="p-12 text-center">
            <div className="w-8 h-8 border-2 border-neutral-300 border-t-[#002FA7] rounded-full animate-spin mx-auto" />
            <p className="text-neutral-500 text-sm mt-3">Chargement des données Analytics...</p>
          </div>
        )}

        {/* Overview Cards */}
        {overview && !loading && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="bg-white border border-neutral-200 p-5">
                <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
                  <Users className="w-4 h-4" />
                  Sessions
                </div>
                <p className="font-mono text-2xl font-bold text-neutral-900">
                  {overview.sessions.toLocaleString('fr-FR')}
                </p>
              </div>
              <div className="bg-white border border-neutral-200 p-5">
                <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
                  <Users className="w-4 h-4" />
                  Utilisateurs
                </div>
                <p className="font-mono text-2xl font-bold text-[#002FA7]">
                  {overview.users.toLocaleString('fr-FR')}
                </p>
              </div>
              <div className="bg-white border border-neutral-200 p-5">
                <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
                  <Eye className="w-4 h-4" />
                  Pages vues
                </div>
                <p className="font-mono text-2xl font-bold text-neutral-900">
                  {overview.page_views.toLocaleString('fr-FR')}
                </p>
              </div>
              <div className="bg-white border border-neutral-200 p-5">
                <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
                  <Clock className="w-4 h-4" />
                  Durée moy.
                </div>
                <p className="font-mono text-2xl font-bold text-neutral-900">
                  {formatDuration(overview.avg_session_duration)}
                </p>
              </div>
              <div className="bg-white border border-neutral-200 p-5">
                <div className="flex items-center gap-2 text-neutral-500 text-sm mb-2">
                  <ArrowUpDown className="w-4 h-4" />
                  Taux de rebond
                </div>
                <p className="font-mono text-2xl font-bold text-neutral-900">
                  {overview.bounce_rate}%
                </p>
              </div>
            </div>

            {/* Daily Chart */}
            {dailyData.length > 0 && (
              <div className="bg-white border border-neutral-200 p-5">
                <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">
                  Trafic quotidien — {selectedClient?.name}
                </h2>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={formatDate}
                      tick={TICK_SMALL}
                    />
                    <YAxis tick={TICK_SMALL} />
                    <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString('fr-FR')} />
                    <Line type="monotone" dataKey="sessions" stroke="#002FA7" strokeWidth={2} dot={false} name="Sessions" />
                    <Line type="monotone" dataKey="users" stroke="#2A9D8F" strokeWidth={2} dot={false} name="Utilisateurs" />
                    <Line type="monotone" dataKey="page_views" stroke="#E9C46A" strokeWidth={2} dot={false} name="Pages vues" />
                  </LineChart>
                </ResponsiveContainer>
                <div className="flex gap-6 justify-center mt-3">
                  <div className="flex items-center gap-2 text-sm"><div className="w-3 h-3 rounded-full bg-[#002FA7]" />Sessions</div>
                  <div className="flex items-center gap-2 text-sm"><div className="w-3 h-3 rounded-full bg-[#2A9D8F]" />Utilisateurs</div>
                  <div className="flex items-center gap-2 text-sm"><div className="w-3 h-3 rounded-full bg-[#E9C46A]" />Pages vues</div>
                </div>
              </div>
            )}

            {/* Sources and Pages side by side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Sources */}
              {sources.length > 0 && (
                <div className="bg-white border border-neutral-200 p-5">
                  <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">
                    Sources de trafic
                  </h2>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={sources} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={TICK_SMALL} />
                      <YAxis 
                        type="category" 
                        dataKey="channel" 
                        width={120}
                        tick={TICK_CATEGORY}
                      />
                      <Tooltip />
                      <Bar dataKey="sessions" fill="#002FA7" name="Sessions" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Top Pages */}
              {topPages.length > 0 && (
                <div className="bg-white border border-neutral-200 p-5">
                  <h2 className="font-heading font-semibold text-lg text-neutral-900 mb-4">
                    Pages les plus visitées
                  </h2>
                  <div className="space-y-2">
                    {topPages.map((page, idx) => (
                      <div key={page.page} className="flex items-center justify-between py-2 border-b border-neutral-50 last:border-0">
                        <div className="flex items-center gap-2 min-w-0 flex-1">
                          <span className="text-xs text-neutral-400 font-mono w-5">{idx + 1}</span>
                          <span className="text-sm text-neutral-700 truncate">{page.page}</span>
                        </div>
                        <div className="flex items-center gap-4 flex-shrink-0 ml-3">
                          <span className="font-mono text-sm text-neutral-900 font-medium">{page.views}</span>
                          <span className="text-xs text-neutral-400">vues</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Config Dialog */}
        <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#002FA7]" />
                Configurer Google Analytics
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-sm text-neutral-500">
                Entrez l'ID de propriété GA4 pour ce client. Vous le trouverez dans Google Analytics &gt; Administration &gt; Propriété &gt; Détails de la propriété.
              </p>
              <div>
                <Label htmlFor="property-id">ID de propriété GA4</Label>
                <Input
                  id="property-id"
                  value={configPropertyId}
                  onChange={(e) => setConfigPropertyId(e.target.value)}
                  placeholder="Ex: 123456789"
                  className="mt-1 font-mono"
                  data-testid="ga-property-input"
                />
                <p className="text-xs text-neutral-400 mt-1">Uniquement les chiffres (ex: 123456789)</p>
              </div>
              <div className="bg-neutral-50 rounded p-3 text-xs text-neutral-500 space-y-1">
                <p className="font-medium text-neutral-700">N'oubliez pas :</p>
                <p>Ajoutez l'email du compte de service comme "Lecteur" dans GA :</p>
                <p className="font-mono text-[#002FA7] break-all">seo-platform-analytics@projetmanagementseo.iam.gserviceaccount.com</p>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>Annuler</Button>
                <Button 
                  className="bg-[#002FA7] hover:bg-[#002277]"
                  onClick={handleSaveProperty}
                  disabled={!configPropertyId.trim()}
                  data-testid="ga-save-property-btn"
                >
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
